import {
    Box,
    Button,
    Flex,
    Heading,
    HStack,
    Image,
    Input,
    InputGroup,
    InputLeftElement,
    SimpleGrid,
    Text,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { ImportSection } from './components/DashboardStyles';
import illustration from '../../assets/Illustration.png';
import { useNavigate } from 'react-router-dom';
import { search } from './components/Icons';
import { useDispatch, useSelector } from 'react-redux';
import { get_wishlist, remove_from_wishlist } from '../../redux/actions/wishActions';
import toast from 'react-hot-toast';
import { add_to_cart } from '../../redux/actions/productActions';
import WishlistBox from 'components/Wishlist/WishlistBox';
import FullPagination from 'components/Dashboard/FullPagination';
import { AiOutlineDelete } from 'react-icons/ai';
import { RxDashboard } from 'react-icons/rx';

const ImportList = () => {
    const navigate = useNavigate();
    const [wishList, setWishList] = useState([]);
    const [searchFilter, setSearchFilter] = useState('');
    const wishReducer = useSelector((state) => state.wishReducer);
    const productReducer = useSelector((state) => state.productReducer);
    const dispatch = useDispatch();
    const { products } = wishReducer;
    const { quoteId } = productReducer;

    useEffect(() => {
        dispatch(get_wishlist());
    }, [dispatch]);

    useEffect(() => {
        if (products?.items) {
            const updatedProducts = products.items?.map((item) => {
                return { ...item, isChecked: false };
            });
            setWishList(updatedProducts);
        } else {
            setWishList([]);
        }
    }, [products]);

    const updateItem = (item) => {
        const updatedListItem = wishList.map((listItem) => {
            if (listItem.item_id === item.item_id) {
                return {
                    ...listItem,
                    isChecked: !listItem.isChecked,
                };
            }
            return listItem;
        });
        setWishList(updatedListItem);
    };

    const selectAllClicked = (value) => {
        const updatedProducts = wishList.map((item) => {
            return { ...item, isChecked: value };
        });
        setWishList(updatedProducts);
    };

    const selectedItems = useMemo(() => {
        const result = wishList?.filter((data) => data.isChecked);
        const length = result.length;
        return { result, length };
    }, [wishList]);

    const addAllToCart = () => {
        let items_sku = selectedItems.result.map((item) => {
            return { sku: item.sku, qty: 1, quoteId };
        });
        for (let i = 0; i < items_sku.length; i++) {
            toast.promise(
                dispatch(add_to_cart(items_sku[i])),
                {
                    loading: 'Adding to cart...',
                    success: <b>Item added to cart</b>,
                    error: <b>Could not add item.</b>,
                },
                { position: 'top-right' },
            );
        }
    };

    const deleteAllFromWishlist = () => {
        let items_sku = selectedItems.result.map((item) => {
            return { productId: item.item_id };
        });
        for (let i = 0; i < items_sku.length; i++) {
            toast.promise(
                dispatch(remove_from_wishlist(items_sku[i])),
                {
                    loading: 'Removing...',
                    success: <b> Item Removed</b>,
                    error: <b>Could not remove item.</b>,
                },
                { position: 'top-right' },
            );
        }
    };

    return (
        <ImportSection>
            {wishList?.length === 0 && (
                <Flex w="full" h="full" justify="center" alignItems="center" flexDir="column">
                    <InputGroup
                        bg="#F2F4F5"
                        w={{ lg: '275px', base: 'full' }}
                        borderRadius="10px"
                        outline="none"
                        border="none">
                        <InputLeftElement pointerEvents="none">{search}</InputLeftElement>
                        <Input
                            type="text"
                            outline="none"
                            border="none"
                            placeholder="Search"
                            value={searchFilter}
                            onChange={(e) =>
                                setSearchFilter(() => {
                                    return e.target.value;
                                })
                            }
                        />
                    </InputGroup>
                    <Image src={illustration} />
                    <Heading
                        as="h2"
                        w="55%"
                        mb="5px"
                        color="#090A0A"
                        fontSize="18px"
                        textAlign="center"
                        lineHeight="24px">
                        You didn’t save any items yet.{' '}
                    </Heading>
                    <Text fontSize="14px" textAlign="center" lineHeight="20px" mt="12px" w="80%">
                        Tap the heart icon near any product and we’ll save it here for you.
                    </Text>
                    <Button
                        w="170px"
                        color="#F5F5F5"
                        bg="#1b3b5f"
                        mt="25px"
                        fontWeight="400"
                        borderRadius="48px"
                        outline="none"
                        h="48px"
                        onClick={() => navigate('/dashboard/products')}>
                        Add Items
                    </Button>
                </Flex>
            )}

            {wishList?.length > 0 && (
                <>
                    <HStack justify="space-between" h="48px" mb="20px" alignItems="center">
                        <Heading fontSize="32px">Wishlist ({wishList.length})</Heading>
                        <Button
                            bg="#1b3b5f"
                            color="#fff"
                            fontWeight="400"
                            _hover={{ opacity: '0.8' }}
                            onClick={() => selectAllClicked(true)}>
                            Select All
                        </Button>
                    </HStack>
                    <SimpleGrid columns={{ base: 1, lg: 3 }} gap="10">
                        {wishList.map((item) => (
                            <WishlistBox item={item} key={item.id} updateItem={updateItem} />
                        ))}
                    </SimpleGrid>

                    {selectedItems.length ? (
                        <Flex
                            mt="10"
                            px="10"
                            alignItems="center"
                            fontSize="sm"
                            fontWeight="medium"
                            borderTop="0.724px solid rgba(0, 0, 0, 0.10)"
                            w="full"
                            pt="4"
                            gap={2}
                            pb={{ base: 24, md: 4 }}
                            flexDirection={{ base: 'column', md: 'row' }}
                            justifyContent="space-between">
                            <Flex alignItems="center" gap="3">
                                <Text>{selectedItems.length} selected</Text>
                                <Button
                                    onClick={() => selectAllClicked(false)}
                                    isDisabled={!selectedItems.length}>
                                    Deselect
                                </Button>
                            </Flex>
                            <Flex gap="3">
                                <Button
                                    bg="#FFF0F0"
                                    leftIcon={<AiOutlineDelete />}
                                    color="#FF0100"
                                    onClick={deleteAllFromWishlist}
                                    _hover={{
                                        bg: '#FF0100',
                                        color: '#FFF0F0',
                                        border: '1px solid #FFF0F0',
                                    }}
                                    fontWeight="400">
                                    Delete
                                </Button>
                                <Button
                                    bg="#1b3b5f"
                                    leftIcon={<RxDashboard />}
                                    onClick={addAllToCart}
                                    color="#fff"
                                    _hover={{
                                        bg: '#fff',
                                        color: '#1b3b5f',
                                        border: '1px solid #1b3b5f',
                                    }}
                                    fontWeight="400">
                                    Add to cart &#40;{selectedItems.length}&#41;
                                </Button>
                            </Flex>
                        </Flex>
                    ) : (
                        <Box pb={{ base: '60px', lg: '20px' }}>
                            <FullPagination handlePageClick={() => null} fullData={wishList} />
                        </Box>
                    )}
                </>
            )}
        </ImportSection>
    );
};

export default ImportList;
